import React, { forwardRef, useMemo } from 'react';
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    Grid,
    Box,
    Link as MuiLink,
} from '@mui/material';

import { CardBlockProps } from '../types';
import { getImageUrl, getColSize } from '../../../utils/helpers';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface CardPersonProps extends CardBlockProps { }

export const CardPerson = forwardRef<HTMLDivElement, CardPersonProps>(
    ({ kedo, block, data, listItems, getValue }, ref) => {
        const templateOptions = block.settings?.templateOptions;
        const col = block.col;

        const fieldConfig = useMemo(() => {
            if (!templateOptions) return null;

            return {
                picture: listItems.find((item) => item.id === templateOptions.picture),
                title: listItems.find((item) => item.id === templateOptions.title),
                description: listItems.find((item) => item.id === templateOptions.description),
            };
        }, [templateOptions, listItems]);

        if (!templateOptions || !fieldConfig) {
            return null;
        }

        const mdCol = getColSize(col, 'md');
        const lgCol = getColSize(col, 'lg');

        const gridMd = Math.floor(12 / (12 / mdCol));
        const gridLg = Math.floor(12 / (12 / lgCol));

        return (
            <Box ref={ref}>
                <Grid container spacing={2}>
                    {data.dossiers.map((dossier) => {
                        const dossierData = data.contents.filter(
                            (item) => item.id === dossier.id
                        );

                        const imageUrl = fieldConfig.picture
                            ? getImageUrl(fieldConfig.picture, dossierData, kedo)
                            : null;

                        return (
                            <Grid size={{ xs: 12, md: gridMd, lg: gridLg }} key={dossier.id}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    {imageUrl && (
                                        <CardMedia
                                            component="img"
                                            image={imageUrl}
                                            alt={dossier.summary}
                                            sx={{
                                                height: 200,
                                                objectFit: 'cover',
                                            }}
                                        />
                                    )}
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography variant="h6" component="h2" gutterBottom>
                                            <MuiLink
                                                href={`/contentdossier/${dossier.id}`}
                                                underline="hover"
                                                color="inherit"
                                            >
                                                {fieldConfig.title
                                                    ? getValue(fieldConfig.title, dossierData, dossier)
                                                    : dossier.summary}
                                            </MuiLink>
                                        </Typography>
                                        {fieldConfig.description && (
                                            <Typography variant="body2" color="text.secondary">
                                                {getValue(fieldConfig.description, dossierData, dossier)}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>
        );
    }
);

CardPerson.displayName = 'CardPerson';
